import { useMemo, useState } from 'react';
import { useRouter } from '@belong/common';
import { Tabs } from '@belong/ui';
import clsx from 'clsx';
import { UnitListingDetailsModel } from 'src/api/models';
import { getMediaSectionNames } from '../../utils';
import { Gallery } from '../gallery/gallery';
import styles from './cover.module.css';
import { Map } from './tabs/map';
import { Photos } from './tabs/photos';
import { StreetView } from './tabs/street-view';
import { Tour3d } from './tabs/tour-3d';
import { VirtualTour } from './tabs/virtual-tour';
import { getAvailableTabs } from './utils';

type CoverProps = {
  home: UnitListingDetailsModel;
  isPreview: boolean;
  hasRoomsPhotos: boolean;
};

export function Cover({ home, isPreview, hasRoomsPhotos }: CoverProps) {
  const availableTabs = getAvailableTabs(home, isPreview, hasRoomsPhotos);
  const router = useRouter();
  const queryTab = router.query.view;
  const queryTabIndex = availableTabs.findIndex((tab) => tab.key === queryTab);

  const [activeTab, setActiveTab] = useState(
    queryTabIndex !== -1 ? queryTabIndex : 0,
  );

  const { address, virtual3DTourProvider, virtual3DTourId } = home;
  const { mediaSectionNames } = useMemo(
    () => getMediaSectionNames(home),
    [home],
  );

  return (
    <div className="w-full overflow-hidden">
      <Tabs
        index={activeTab}
        onChange={(tab) => {
          router.query.view = availableTabs[tab]?.key;
          router.push(router, undefined, { shallow: true });
          setActiveTab(tab);
          window.scrollTo(0, 0);
        }}
        isDraggable
        activeClassname="overflow-visible bottom-0"
      >
        <div className={clsx('w-full', styles.coverContainer)}>
          <Tabs.PanelList className="h-full">
            {Object.entries(availableTabs).map(([key, value], index) => {
              return (
                <Tabs.PanelItem className="h-full pt-0" key={key} index={index}>
                  {value.key === 'photos' && (
                    <Photos home={home} isPreview={isPreview} />
                  )}
                  {/* `!` throughout: a tab only reaches this point when
                      `getAvailableTabs` found the media backing it, so the
                      3D-tour ids and the address are present by construction —
                      and every one of these was already dereferenced
                      unconditionally by the component receiving it. */}
                  {value.key === '3D-tour' && (
                    <Tour3d
                      virtual3DTourProvider={virtual3DTourProvider!}
                      virtual3DTourId={virtual3DTourId!}
                    />
                  )}
                  {value.key === 'map' && (
                    <Map coordinates={address!.coordinates!} />
                  )}
                  {value.key === 'street' && (
                    <StreetView coordinates={address!.coordinates!} />
                  )}
                  {value.key === 'virtual-tour' && <VirtualTour home={home} />}
                </Tabs.PanelItem>
              );
            })}
          </Tabs.PanelList>
        </div>
        <div className={clsx('my-2sm w-full overflow-x-scroll', styles.tabs)}>
          <div className="min-w-max">
            <Tabs.TabList className="mb-md flex items-center justify-center">
              {Object.entries(availableTabs).map(([key, value], index) => {
                return (
                  <Tabs.TabItem className="border-2" key={key} index={index}>
                    {value.text}
                  </Tabs.TabItem>
                );
              })}
            </Tabs.TabList>
          </div>
        </div>
      </Tabs>

      {/* The Rooms tab has no inline panel — selecting it opens the gallery modal. Match on the
          active tab's key rather than a hardcoded index: Rooms is only the second tab when the
          home also has exterior photos, and it moves whenever a tab ahead of it is unavailable
          (which is exactly what restoring the Coming Soon tabs above changes). */}
      {availableTabs[activeTab]?.key === 'rooms' && (
        <Gallery
          home={home}
          mediaSections={mediaSectionNames}
          onDismiss={() => setActiveTab(0)}
        />
      )}
    </div>
  );
}
